/** @format */

import { useState } from 'react';

import cn from 'classnames';
import { isStringValid } from 'core-lib/utils/utils';
import dayjs from 'dayjs';
import Calendar from 'ui-v2/components/Calendar';
import Modal from 'ui-v2/components/Modal';
import { Typography } from 'ui-v2/components/Typography';
import s from './DatePicker.module.scss';
import Icon from '../../Icon/Icon';

export type DatePickerProps = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  disablePreviousDates?: boolean;
  disableFutureDates?: boolean;
  leftIcon?: React.JSX.Element;
  rightIcon?: React.JSX.Element;
  minDate?: string;
  maxDate?: string;
  dateRenderFormat?: string;
  zIndex?: number;
  hideModalOnSelect?: boolean;
  customClassName?: string;
  hasError?: boolean;
  errorText?: string;
  helperText?: string;
  required?: boolean;
  showRequiredAsterisk?: boolean;
};

export const DatePicker = ({
  label = '',
  placeholder = 'Select date...',
  zIndex = 9999,
  disabled,
  leftIcon,
  rightIcon,
  disablePreviousDates,
  disableFutureDates,
  minDate,
  maxDate,
  onChange,
  value,
  customClassName,
  hasError,
  errorText,
  helperText,
  required,
  showRequiredAsterisk,
  dateRenderFormat,
}: DatePickerProps) => {
  const [isPickerVisible, setIsPickerVisible] = useState(false);

  const handleDateSelect = (date: string) => {
    onChange(date ? date : null);
    setIsPickerVisible(false);
  };

  return (
    <>
      <div className={cn(s.container, customClassName)}>
        {label && (
          <div className={s.label}>
            <Typography color="tertiary" label={label} variant="label_3_medium" />
            {required && showRequiredAsterisk && (
              <Typography label="*" variant="body_3_medium" color="negative" />
            )}
          </div>
        )}

        {/* Button to Open Modal */}
        <button
          className={cn(s.input, {
            [s.has_error]: hasError,
            [s.disabled]: disabled,
          })}
          type="button"
          onClick={() => {
            if (!disabled) {
              setIsPickerVisible(true);
            }
          }}
          aria-label="Open date picker"
          role="button"
        >
          <div className={s.wrapText}>
            <div className={s.left_icon}>
              {leftIcon || <Icon name="CalenderRegular" height={20} width={20} color="tertiary" />}
            </div>
            <Typography
              label={(value && dayjs(value).format(dateRenderFormat)) || placeholder}
              variant={'label_3_medium'}
              color={value ? 'primary' : 'tertiary'}
            />
            {rightIcon && <div className={s.right_icon}>{rightIcon}</div>}
          </div>
        </button>

        <div className={s.textWrapper}>
          {hasError && isStringValid(errorText) && (
            <div className={s.error}>
              <Icon name="ShieldWarningRegular" width={16} height={16} color="negative" />
              <Typography
                label={errorText}
                variant="label_4_medium"
                color="negative"
                style={{ marginTop: '1px' }}
              />
            </div>
          )}

          {!hasError && isStringValid(helperText) && (
            <div className={s.helperText}>
              <Icon name="InfoCircleRegular" width={16} height={16} color="tertiary" />
              <Typography
                label={helperText}
                variant="label_4_medium"
                color="tertiary"
                style={{ marginTop: '1px' }}
              />
            </div>
          )}
        </div>

        {/* Pikaday Calendar Inside Modal */}
        <Modal
          show={isPickerVisible}
          head={{
            show: false,
            title: '',
            onClose: () => setIsPickerVisible(false),
          }}
          variant="bottomSheet"
          closeOnClickOutside
          noPadding
          animation={{ type: 'btt', timeout: 's' }}
          rootStyle={{ zIndex }}
          blurredBg
        >
          <div className={s.modal}>
            <Calendar
              onSelect={handleDateSelect}
              selectedDate={value || new Date().toString()}
              disablePreviousDates={disablePreviousDates}
              disableFutureDates={disableFutureDates}
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>
        </Modal>
      </div>
    </>
  );
};
